import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Colors = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎨 CSS Colors</h1>

      <p className="tutorial-intro">
        CSS colors allow you to style text, backgrounds, borders, and even
        shadows. You can use named colors, HEX values, RGB, RGBA, HSL, and HSLA.
        Colors make your web pages more attractive and user-friendly.
      </p>

      {/* Color Names */}
      <div className="tutorial-section">
        <h2 className="section-title">1. Color Names</h2>
        <p>
          CSS supports around 140 predefined color names like <code>red</code>,{" "}
          <code>blue</code>, <code>green</code>, <code>tomato</code>,{" "}
          <code>gold</code>, etc.
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: red;
}

p {
  background-color: lightgray;
}`}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              background: "tomato",
              padding: "10px",
              color: "white",
              borderRadius: "8px",
            }}
          >
            Tomato
          </div>
          <div
            style={{ background: "gold", padding: "10px", borderRadius: "8px" }}
          >
            Gold
          </div>
          <div
            style={{
              background: "lightblue",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            LightBlue
          </div>
        </div>
      </div>

      {/* HEX Values */}
      <div className="tutorial-section">
        <h2 className="section-title">2. HEX Values</h2>
        <p>
          HEX codes use <code>#RRGGBB</code>. Each pair defines red, green, and
          blue using values from <code>00</code> to <code>FF</code>.
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: #ff0000; /* Red */
}

p {
  background-color: #00ff00; /* Green */
}`}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              background: "#ff0000",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            #ff0000
          </div>
          <div
            style={{
              background: "#00ff00",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            #00ff00
          </div>
          <div
            style={{
              background: "#0000ff",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            #0000ff
          </div>
        </div>
      </div>

      {/* RGB Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">3. RGB Colors</h2>
        <p>
          RGB specifies colors using <strong>red, green, blue</strong> values
          between 0 and 255.
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: rgb(0, 0, 255); /* Blue */
}

p {
  background-color: rgb(255, 255, 0); /* Yellow */
}`}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              background: "rgb(255,0,0)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            rgb(255,0,0)
          </div>
          <div
            style={{
              background: "rgb(0,255,0)",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            rgb(0,255,0)
          </div>
          <div
            style={{
              background: "rgb(0,0,255)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            rgb(0,0,255)
          </div>
        </div>
      </div>

      {/* RGBA Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">4. RGBA Colors</h2>
        <p>
          RGBA is like RGB but includes an <strong>alpha</strong> value (0–1)
          for transparency.
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: rgba(255, 0, 0, 0.5); /* Red with 50% transparency */
}`}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              background: "rgba(255,0,0,0.3)",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            rgba(255,0,0,0.3)
          </div>
          <div
            style={{
              background: "rgba(0,128,0,0.5)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            rgba(0,128,0,0.5)
          </div>
          <div
            style={{
              background: "rgba(0,0,255,0.7)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            rgba(0,0,255,0.7)
          </div>
        </div>
      </div>

      {/* HSL Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">5. HSL & HSLA Colors</h2>
        <p>
          HSL stands for Hue (0–360), Saturation (0–100%), and Lightness
          (0–100%). HSLA adds an alpha channel.
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: hsl(120, 100%, 50%); /* Bright Green */
}

p {
  background-color: hsla(240, 100%, 50%, 0.3); /* Semi-transparent Blue */
}`}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <div
            style={{
              background: "hsl(0,100%,50%)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            hsl(0,100%,50%)
          </div>
          <div
            style={{
              background: "hsl(120,100%,40%)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            hsl(120,100%,40%)
          </div>
          <div
            style={{
              background: "hsl(240,100%,50%)",
              padding: "10px",
              color: "#fff",
              borderRadius: "8px",
            }}
          >
            hsl(240,100%,50%)
          </div>
        </div>
      </div>

      {/* Applying Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">6. Where to Use Colors</h2>
        <ul className="tutorial-list">
          <li>
            <code>color</code> → Text color
          </li>
          <li>
            <code>background-color</code> → Element background
          </li>
          <li>
            <code>border-color</code> → Borders
          </li>
          <li>
            <code>box-shadow</code> → Shadows
          </li>
        </ul>
        <CodeCopyBlock
          code={`p {
  color: white;
  background-color: black;
  border: 2px solid red;
  box-shadow: 3px 3px 10px gray;
}`}
        />
      </div>

      {/* Gradients */}
      <div className="tutorial-section">
        <h2 className="section-title">7. Gradients (Bonus)</h2>
        <p>
          CSS also allows you to create beautiful gradient backgrounds with{" "}
          <code>linear-gradient()</code> or <code>radial-gradient()</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  background: linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet);
}`}
        />
        <div
          style={{
            height: "60px",
            borderRadius: "8px",
            marginTop: "1rem",
            background:
              "linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)",
          }}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you’ve mastered CSS colors, move on to{" "}
          <a href="/css/backgrounds" className="learn-more">
            CSS Backgrounds
          </a>{" "}
          to learn about background images, patterns, and advanced effects.
        </p>
      </div>
    </div>
  );
};

export default CSS_Colors;
